import styled from '@emotion/styled';
import { Select, Form } from 'antd';
import FormField from 'components/FormField';
import HintTextarea from 'components/HintTextarea';
import { Formik, FormikConfig, FormikProps, useFormikContext } from 'formik';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { Image } from 'views/Management/Feedback/components/FeedbackTable';
import useCaseCategory, { CategoryMap } from 'views/Management/Feedback/hooks/useCaseCategory';

export type FeedbackFormValues = Util.PartialPartial<
  Feedback.ReportCase,
  'caseCategory' | 'reason'
>;

type FV = FeedbackFormValues;

export interface FeedbackFromProps extends FormikConfig<FV> {
  children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
  storeName?: string;
}

const reasonLimit = 100;

function FeedbackForm(props: FeedbackFromProps) {
  const { children, storeName, ...otherProps } = props;

  const { t } = useTranslate();

  const { loading: categoryListLoading, list: categoryList, categoryMap } = useCaseCategory();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof FV, string>> = {};

      const { caseType, caseCategory, reason } = values;

      if (!caseCategory) {
        errors.caseCategory = t('scene.chatWorkspace.feedbackDialog.validation.categoryRequired');
      }

      const caseMap = caseType && caseType !== 'ACTIVITY' ? categoryMap[caseType] : undefined;

      const reasonRequire = caseCategory && caseMap?.[caseCategory]?.requireReason;

      if (reasonRequire && (!reason || !reason.trim())) {
        errors.reason = t('scene.chatWorkspace.feedbackDialog.validation.descriptionRequired');
      } else if (reason && reason.length > reasonLimit) {
        errors.reason = t('scene.chatWorkspace.feedbackDialog.validation.descriptionMaxLength', {
          x: reasonLimit,
        });
      }

      return errors;
    },
    [categoryMap, t],
  );

  return (
    <Formik enableReinitialize validate={validate} {...otherProps}>
      {(props) => {
        const form = (
          <FormContent
            categoryList={categoryList || []}
            categoryListLoading={categoryListLoading}
            categoryMap={categoryMap}
            storeName={storeName}
          />
        );

        return children(form, props);
      }}
    </Formik>
  );
}

const MessagePreview = styled.div`
  position: relative;
  padding: 8px 8px 8px 16px;
  color: #84868c;
  background: #f7f9fa;
  border-radius: 2px;
  > p {
    margin: 0;
  }
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 8px;
    left: 8px;
    width: 2px;
    height: calc(100% - 16px);
    background: #e1e4eb;
  }
`;

const CategorySelect = styled.div`
  width: 260px;
`;

const ReasonInput = styled.div`
  width: 400px;
`;

export interface FormContentProps {
  categoryList: Feedback.CaseCategory[];
  categoryListLoading: boolean;
  categoryMap: CategoryMap;
  storeName?: string;
}

function FormContent(props: FormContentProps) {
  const { categoryList, storeName, categoryListLoading, categoryMap } = props;

  const { t } = useTranslate();

  const { values } = useFormikContext<FV>();

  const { caseType, caseCategory, message } = values;

  const { fromUserName, msgFromType } = message || {};

  const isRobotMessage = msgFromType === 'BOT';
  const showName = isRobotMessage
    ? `${storeName}-${t('domain.aiReception.agent.lechat')}`
    : fromUserName;

  const messagePreview = message ? (
    <Form.Item>
      <MessagePreviewer message={message} showName={showName} />
    </Form.Item>
  ) : null;

  const systemLanguage = useSystemLanguageValue();

  const categoryOptions = useMemo(() => {
    return categoryList.reduce<{ value: string; label: string }[]>((res, category) => {
      const { type, label: langLabelMap, caseType: currentCaseType } = category;

      const { [systemLanguage]: label } = langLabelMap;

      if (currentCaseType === caseType) {
        res.push({
          value: type,
          label,
        });
      }

      return res;
    }, []);
  }, [categoryList, caseType, systemLanguage]);

  const categorySelect = (
    <FormField<FV['caseCategory']>
      name="caseCategory"
      label={t('scene.chatWorkspace.feedbackDialog.field.category')}
      required
    >
      {({ field }) => {
        return (
          <CategorySelect>
            <Select
              placeholder={t('scene.chatWorkspace.feedbackDialog.placeholder.selectCategory')}
              loading={categoryListLoading}
              options={categoryOptions}
              getPopupContainer={(trigger) => trigger}
              {...field}
            />
          </CategorySelect>
        );
      }}
    </FormField>
  );

  const currentCategory =
    caseType && caseType !== 'ACTIVITY' && caseCategory
      ? categoryMap[caseType]?.[caseCategory]
      : undefined;

  const reasonInput = currentCategory ? (
    <FormField<FV['reason']>
      name="reason"
      label={t('scene.chatWorkspace.feedbackDialog.field.description')}
      required={currentCategory.requireReason}
    >
      {({ field }) => {
        return (
          <ReasonInput>
            <HintTextarea
              placeholder={t('scene.chatWorkspace.feedbackDialog.placeholder.inputDescription')}
              limit={100}
              {...field}
            />
          </ReasonInput>
        );
      }}
    </FormField>
  ) : null;

  return (
    <Form layout="vertical">
      {messagePreview}
      {categorySelect}
      {reasonInput}
    </Form>
  );
}

export function MessagePreviewer({
  message,
  showName,
}: {
  message: Conversation.Message;
  showName: ReactNode;
}) {
  const { t } = useTranslate();

  const { messageType, textContent, imageContent, imageWithTextContent } = message;

  const messageTypeMap: Partial<Record<Conversation.MessageType, ReactNode>> = {
    TEXT: <p>{textContent?.originalText || textContent?.text}</p>,
    IMAGE_WITH_TEXT: <p>{`[${t('domain.message.type.image')}]${imageWithTextContent?.text}`}</p>,
    IMAGE: <Image src={imageContent?.imgUrl || imageContent?.externalImgUrl} alt="messageImage" />,
  };

  const MESSAGE_TYPE_KEY_MAP: Record<string, string> = {
    IMAGE: 'domain.message.type.image',
    VIDEO: 'domain.message.type.video',
    STICKER: 'domain.message.type.sticker',
    ITEM: 'domain.message.type.product',
    FAQ_LIVEAGENT: 'domain.message.type.faqLiveAgent',
    VARIATION_CARD: 'domain.message.type.variationCard',
    SYS_ASSIGN: 'domain.message.systemType.assign',
    SYS_WA_REJECT: 'domain.message.systemType.whatsAppRejected',
    SYS_INIT_CONVERSATION: 'scene.chatWorkspace.messageThread.systemMessage.initiatedConversation',
    SYS_FAQ_QUESTION: 'scene.chatWorkspace.messageThread.systemMessage.faqQuestion',
    SHOPEE_RETURN_REFUND_CARD: 'scene.chatWorkspace.messageThread.systemMessage.shopeeReturnRefund',
    recall: 'scene.chatWorkspace.messageThread.systemMessage.recall',
  };

  const getMessageTypeKey = (type: string): string =>
    MESSAGE_TYPE_KEY_MAP[type] || 'scene.chatWorkspace.messageThread.feedback.unknownMessageType';

  const { [messageType]: messageContent = <p>[{t(getMessageTypeKey(messageType))}]</p> } =
    messageTypeMap;

  return (
    <MessagePreview>
      <div>{showName}:</div>
      {messageContent}
    </MessagePreview>
  );
}

export default FeedbackForm;
